import { Button, ChipConfirmModal, Modal, ModalContent, ModalDescription } from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { waitForSubscription } from "~/common/billing/paygate";
import { PlanChooser } from "~/common/billing/plans";
import { openSubscriptionCheckout } from "~/common/billing/subscription-checkout";
import { CreditsStat } from "~/common/billing/topup";
import { whyDisabled } from "~/core/access/index";
import { ApiError, api, type Org, type Plan, type PlanPreview } from "~/core/api/index";
import * as fmt from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { ModalHeader } from "~/core/ui/dialog";
import { Stat } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { readingPath } from "~/modules/analytics/lib/loop";

/**
 * The plan: what the account is on, and what it could move to.
 *
 * Its own section rather than a block on the bill, because it is the only
 * screen in the panel where somebody is deciding rather than reading. The
 * bill — credits spent, models called — is the record of a decision already
 * made, and reading a record next to a price list makes both harder to do.
 *
 * The balance sits here too, split by lifetime, because the two ways of
 * getting more credits both start on this screen: a bigger plan, or a pack.
 */
export function SubscriptionPanel({ org, admin }: { org: Org; admin: boolean }) {
	const t = useT();
	const { locale } = useI18n();
	const queryClient = useQueryClient();
	const billing = useQuery({
		queryKey: [...keys.org(org.id), "billing"],
		queryFn: () => api.billing(org.id),
		...read,
	});
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.org(org.id) });
	const changePlan = useMutation({ mutationFn: (key: string) => api.changePlan(org.id, key), onSuccess: refresh });
	const cancelPlan = useMutation({ mutationFn: () => api.cancelSubscription(org.id), onSuccess: refresh });
	const keepPlan = useMutation({ mutationFn: () => api.cancelPlanChange(org.id), onSuccess: refresh });
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<unknown>(null);
	// The downgrade being considered: the plan and what the server said about
	// it. Null is no dialog.
	const [down, setDown] = useState<{ plan: Plan; preview: PlanPreview } | null>(null);

	const subscription = billing.data?.subscription ?? null;
	const pending = subscription?.pending_plan_key ?? null;
	const [cancelling, setCancelling] = useState(false);
	const nameOf = (key: string | null | undefined) => {
		const found = billing.data?.plans.find((p) => p.key === key);
		return found ? pick(locale, [found.name_en, found.name_zh]) : (key ?? "");
	};
	const expired = (billing.data?.subscription_state ?? org.subscription_state) === "expired";
	// The plan's name, not its key: `pro` is what the database calls it, and a
	// customer reading their own bill should see what the pricing page called
	// it, in the language they are reading.
	const plan = billing.data?.current;
	const planName = pick(locale, [plan?.name_en, plan?.name_zh]) ?? org.plan_key;
	// The status is a word the server stores, not a word to show: `past_due`
	// under a customer's own plan name should read as a sentence they can act
	// on.
	// "Renews" is a promise, and a subscription already scheduled to end — a
	// cancellation, or the trial month — does not keep it.
	const period = subscription
		? `${t(`billing.status.${subscription.status}`)} · ${t(
				subscription.cancel_at_period_end ? "billing.periodEnds" : "billing.periodEnd",
			)}: ${fmt.date(locale, subscription.current_period_end)}`
		: t("billing.noSubscription");
	const credits = billing.data?.credits;

	/*
	 * The checkout is not what creates the subscription — the webhook behind it
	 * is, and it lands a second or two after the customer is back on this
	 * screen. Without waiting for it, a success message sits above a panel
	 * still showing the plan they just paid to leave.
	 */
	async function choose(planKey: string, interval: "month" | "year") {
		setBusy(planKey);
		setError(null);
		try {
			const checkout = await api.subscribe(org.id, {
				plan_key: planKey,
				interval,
				currency: "USD",
			});
			const completed = await openSubscriptionCheckout(checkout.checkout_url, t("billing.checkoutUnavailable"));
			if (completed) {
				const confirmed = await waitForSubscription(async () => {
					const data = await api.billing(org.id);
					queryClient.setQueryData([...keys.org(org.id), "billing"], data);
					return data.subscription_state === "active";
				});
				toast.info(t(confirmed ? "billing.paid" : "billing.confirming"));
			}
			refresh();
		} catch (e) {
			setError(e);
		} finally {
			setBusy(null);
		}
	}

	/*
	 * Moving down is asked about before it is done. The server's preview says
	 * whether the account fits the smaller plan; when it does not, the dialog
	 * lists what has to go and where to go to remove it, and there is no
	 * button that removes it here — those are the customer's decisions.
	 */
	async function considerDowngrade(planKey: string) {
		const plan = billing.data?.plans.find((p) => p.key === planKey);
		if (!plan) return;
		setBusy(planKey);
		setError(null);
		try {
			const preview = await api.planPreview(org.id, planKey);
			setDown({ plan, preview });
		} catch (e) {
			setError(e);
		} finally {
			setBusy(null);
		}
	}

	async function scheduleDowngrade() {
		if (!down) return;
		setBusy(down.plan.key);
		try {
			const result = await changePlan.mutateAsync(down.plan.key);
			toast.success(t("downgrade.done", { date: fmt.date(locale, result.effective_at) }));
			setDown(null);
		} catch (e) {
			setError(e);
			setDown(null);
		} finally {
			setBusy(null);
		}
	}

	// Cancelling is one of the ways out of a lapsed account, so it is offered
	// whatever the gate says — and it stops at the end of the period already
	// paid for rather than taking the rest of the month away.
	async function cancelSubscription() {
		setBusy("cancel");
		try {
			const result = await cancelPlan.mutateAsync();
			toast.success(t("billing.cancelDone", { date: fmt.date(locale, result.effective_at) }));
		} catch (e) {
			setError(e);
		} finally {
			setBusy(null);
			setCancelling(false);
		}
	}

	async function undoDowngrade() {
		setBusy("undo");
		try {
			await keepPlan.mutateAsync();
			toast.success(t("downgrade.undone"));
		} catch (e) {
			setError(e);
		} finally {
			setBusy(null);
		}
	}

	return (
		<>
			{/* A lapsed plan is the first thing on this screen, above the
			    number, because it is the reason the number stopped moving. */}
			{expired ? (
				<div className="banner banner-danger">
					<div>
						<strong>{t("billing.expiredTitle")}</strong>
						<p style={{ margin: "var(--s-2) 0 0" }}>{t("billing.expiredBody")}</p>
					</div>
				</div>
			) : null}

			<div className="stats">
				<Stat
					label={t("billing.plan")}
					value={planName}
					mono={false}
					action={
						subscription && !expired && !subscription.cancel_at_period_end ? (
							<Button
								variant="default"
								disabled={busy !== null || !admin}
								title={whyDisabled(admin, t("access.admin"))}
								onClick={() => setCancelling(true)}
							>
								{t("billing.cancelShort")}
							</Button>
						) : null
					}
					foot={
						pending
							? `${period} · ${t("downgrade.scheduled", { plan: nameOf(pending), date: fmt.date(locale, subscription?.current_period_end) })}`
							: period
					}
				/>
				<CreditsStat
					org={org}
					credits={credits}
					state={expired ? "expired" : (billing.data?.subscription_state ?? org.subscription_state)}
				/>
			</div>

			{error ? (
				<div className="banner banner-warn">{error instanceof ApiError ? error.localized(locale) : String(error)}</div>
			) : null}

			{pending ? (
				<div className="banner">
					{t("downgrade.scheduled", {
						plan: nameOf(pending),
						date: fmt.date(locale, subscription?.current_period_end),
					})}
					<Button
						variant="default"
						className="spacer"
						disabled={busy !== null || !admin}
						title={whyDisabled(admin, t("access.admin"))}
						onClick={undoDowngrade}
					>
						{t("downgrade.undo")}
					</Button>
				</div>
			) : null}

			{/* The trial is born ending, so the ordinary "you cancelled" sentence
			    would be news to somebody who cancelled nothing. */}
			{/* Only while it is still running: after it lapses the red banner at
			    the top is the sentence, and "ends on" in the past is noise. */}
			{subscription?.cancel_at_period_end && !expired ? (
				<div className="banner banner-warn">
					{t(org.trial ? "billing.trialEnds" : "billing.cancelScheduled", {
						date: fmt.date(locale, subscription.current_period_end),
					})}
				</div>
			) : null}

			<ChipConfirmModal
				open={cancelling}
				onOpenChange={(open) => !open && setCancelling(false)}
				title={t("billing.cancelTitle")}
				text={t("billing.cancelBody", { date: fmt.date(locale, subscription?.current_period_end) })}
				dismissLabel={t("billing.cancelKeep")}
				confirm={{ label: t("billing.cancel"), variant: "destructive", onClick: cancelSubscription }}
			/>

			<Async state={billing}>
				{(data) => (
					<PlanChooser
						plans={data.plans}
						// A lapsed account is offered every plan: what it was on is
						// not what it is on, and "upgrade" from nothing is a subscribe.
						current={expired ? undefined : (data.current?.key ?? org.plan_key)}
						busy={busy}
						onChoose={choose}
						// Down is only offered to a live subscription: a lapsed one
						// has nothing to move, and picks a plan afresh above.
						onDowngrade={!expired && subscription?.status === "active" ? considerDowngrade : undefined}
						pending={pending}
						admin={admin}
						trialUsed={data.trial_used}
					/>
				)}
			</Async>

			<DowngradeModal
				state={down}
				current={planName}
				busy={busy !== null}
				onConfirm={scheduleDowngrade}
				onClose={() => setDown(null)}
			/>
		</>
	);
}

/**
 * The move down, before it is scheduled: when it lands, and whether the
 * account fits. When it does not, each thing that is over is a line with a
 * way to the screen where it is trimmed — and the confirm button is not
 * there at all, because there is nothing to confirm yet.
 */
function DowngradeModal({
	state,
	current,
	busy,
	onConfirm,
	onClose,
}: {
	state: { plan: Plan; preview: PlanPreview } | null;
	current: string;
	busy: boolean;
	onConfirm: () => void;
	onClose: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const plan = state ? pick(locale, [state.plan.name_en, state.plan.name_zh]) : "";
	const date = fmt.date(locale, state?.preview.effective_at);

	return (
		<Modal open={state !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
			<ModalContent>
				<ModalHeader>{t("downgrade.title", { plan })}</ModalHeader>
				<ModalDescription>{t("downgrade.when", { current, plan, date })}</ModalDescription>
				{state?.preview.fits ? (
					<p>{t("downgrade.fits")}</p>
				) : (
					<>
						<p>
							<strong>{t("downgrade.overTitle")}</strong>
						</p>
						<p className="muted">{t("downgrade.overBody")}</p>
						<ul className="lede-list">
							{state?.preview.over.map((item) => {
								const where =
									item.what === "questions" && item.entity_id
										? readingPath(`/w/${item.entity_id}`, "prompts")
										: item.what === "engines" && item.entity_id
											? `/w/${item.entity_id}/settings`
											: null;
								return (
									<li key={item.what}>
										{t(`downgrade.over.${item.what}`, {
											have: String(item.have),
											max: String(item.max),
											name: item.entity_name ?? "",
										})}
										{" — "}
										{where ? (
											<Link to={where} onClick={onClose}>
												{t(`downgrade.fix.${item.what}`)}
											</Link>
										) : (
											<span className="muted">{t(`downgrade.fix.${item.what}`)}</span>
										)}
									</li>
								);
							})}
						</ul>
					</>
				)}
				<div className="row-actions" style={{ justifyContent: "flex-end" }}>
					<Button variant="default" onClick={onClose} disabled={busy}>
						{t("downgrade.cancel")}
					</Button>
					{state?.preview.fits ? (
						<Button onClick={onConfirm} disabled={busy}>
							{busy ? t("login.working") : t("downgrade.confirm")}
						</Button>
					) : null}
				</div>
			</ModalContent>
		</Modal>
	);
}
